"use client";

import { Translate } from "@/components/Translate";
import { ArticleCard } from "@/components/ui/ArticleCard";
import { withSearchParams } from "@/components/withSearchParams";
import type { StoriesRoute } from "@/data/routes/stories.route";
import { ProductGrid } from "@/features/products/ProductListingPage/Components";
import { useProducts } from "@/features/products/ProductListingPage/ListingContext";
import { ProductList } from "@/features/products/ProductListingPage/ProductList";
import { Link } from "@/i18n/Link";
import { useLang } from "@/i18n/client";
import { useGeo } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";
import { clsx } from "@frend-digital/ui";
import { useDebouncedValue } from "@frend-digital/ui/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { rpc } from "#rpc";
import { type SearchTab, getActiveTab } from "../utils/searchTabs";
import { DisplayProductsOrNoResults } from "./DisplayProductsOrNoResults";
import styles from "./SearchPage.module.css";

export const SearchResults = withSearchParams<{ noResults?: React.ReactNode }>(
	({ noResults, searchParams }) => {
		const query = searchParams.get("q") || "";
		const debouncedQuery = useDebouncedValue(query, 300);

		const { total: productTotal, isLoading: isLoadingProducts } = useProducts();
		const storiesQuery = useStorySearch(debouncedQuery);
		const { categories } = useCategorySearch(debouncedQuery);

		const articles = (storiesQuery.data || []).filter(
			(story) => "component" in story && story.component === "articlePage",
		);
		const pages = (storiesQuery.data || []).filter(
			(story) => "component" in story && story.component === "page",
		);

		if (!debouncedQuery) {
			return (
				<div className={styles.container}>
					<p>
						<Translate>Enter a search term to see results</Translate>
					</p>
				</div>
			);
		}

		const requestedTab = searchParams.get("tab") as SearchTab | null;
		const currentTab = getActiveTab(requestedTab, {
			products: productTotal,
			articles: articles.length,
			pages: pages.length,
			categories: categories.length,
			isLoadingProducts,
		});

		return (
			<>
				{currentTab === "products" && <ProductListingResults noResults={noResults} />}
				{currentTab === "articles" && <ArticleResults articles={articles} noResults={noResults} />}
				{currentTab === "pages" && <PageResults pages={pages} noResults={noResults} />}
				{currentTab === "categories" && (
					<CategoryResults categories={categories} noResults={noResults} />
				)}
			</>
		);
	},
);

export const useSearchCounts = (searchParams: URLSearchParams) => {
	const query = searchParams.get("q") || "";
	const debouncedQuery = useDebouncedValue(query, 300);

	const { total: productTotal, isLoading: isLoadingProducts } = useProducts();
	const storiesQuery = useStorySearch(debouncedQuery);
	const { categories } = useCategorySearch(debouncedQuery);

	const articles = (storiesQuery.data || []).filter(
		(story) => "component" in story && story.component === "articlePage",
	);
	const pages = (storiesQuery.data || []).filter(
		(story) => "component" in story && story.component === "page",
	);

	return {
		products: productTotal || 0,
		articles: articles.length,
		pages: pages.length,
		categories: categories.length,
		isLoadingProducts,
	};
};

function useStorySearch(query: string) {
	const language = useLang();
	const { country } = useGeo();

	return useQuery({
		queryKey: ["unified-search-stories", language, query, country],
		queryFn: async () => {
			if (!query) return [];
			return rpc.stories.search({ q: query, limit: 4, language, country });
		},
		placeholderData: keepPreviousData,
		refetchOnMount: false,
	});
}

function useCategorySearch(query: string) {
	const language = useLang();
	const { country } = useGeo();

	const categoriesQuery = useQuery({
		queryKey: ["unified-search-categories", language, query, country],
		queryFn: async () => {
			if (!query) return [];

			return rpc.stories.searchCategories({ q: query, language, limit: 20, country });
		},
		placeholderData: keepPreviousData,
		refetchOnMount: false,
	});

	return { categories: categoriesQuery.data || [], isLoadingCategories: categoriesQuery.isLoading };
}

const ProductListingResults = ({ noResults }: { noResults?: React.ReactNode }) => {
	return (
		<DisplayProductsOrNoResults
			productList={
				<ProductGrid>
					<ProductList content={[]} preview={false} />
				</ProductGrid>
			}
			noResults={noResults}
		/>
	);
};

const ArticleResults = ({
	articles,
	noResults,
}: { articles: StoriesRoute["search"]; noResults?: React.ReactNode }) => {
	if (articles.length === 0) {
		return <>{noResults}</>;
	}

	return (
		<div className={styles.resultGrid}>
			{articles.map((article) => (
				<ArticleCard
					key={article.full_slug}
					image={article.image}
					title={article.name}
					description={"description" in article ? article.description : undefined}
					slug={article.full_slug}
					className={styles.articleItem}
				/>
			))}
		</div>
	);
};

const PageResults = ({
	pages,
	noResults,
}: { pages: StoriesRoute["search"]; noResults?: React.ReactNode }) => {
	if (pages.length === 0) {
		return <>{noResults}</>;
	}

	return (
		<div className={styles.resultGrid}>
			{pages.map((page) => (
				<PageResultCard key={page.full_slug} page={page} />
			))}
		</div>
	);
};

const PageResultCard = ({ page }: { page: StoriesRoute["search"][number] }) => {
	const language = useLang();
	const link = sbLink(page.full_slug);

	if (!link) return null;

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);

		return date.toLocaleDateString(language, {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<Link href={link.href} className={clsx(styles.resultCard)}>
			<div className={styles.resultInfo}>
				<h4 className={styles.resultTitle}>{page.name}</h4>
				<div className={styles.resultMeta}>
					{page.publishedAt && <span>{formatDate(page.publishedAt)}</span>}
				</div>
			</div>
		</Link>
	);
};

const CategoryResults = ({
	categories,
	noResults,
}: { categories: Awaited<ReturnType<typeof rpc.stories.links>>; noResults?: React.ReactNode }) => {
	if (categories.length === 0) {
		return <>{noResults}</>;
	}

	return (
		<div className={styles.resultGrid}>
			{categories.map((category) => (
				<CategoryResultCard key={category.slug} category={category} />
			))}
		</div>
	);
};

const CategoryResultCard = ({
	category,
}: { category: Awaited<ReturnType<typeof rpc.stories.links>>[number] }) => {
	const link = sbLink(category.slug);

	if (!link) return null;

	const getParentCategory = (slug: string) => {
		const segments = slug.split("/");
		return segments.length > 2 ? segments[1] : null;
	};

	const parentCategory = getParentCategory(category.slug);

	return (
		<Link href={link.href} className={clsx(styles.resultCard)}>
			<div className={styles.resultInfo}>
				<h4 className={styles.resultTitle}>{category.name}</h4>
				<div className={styles.resultMeta}>{parentCategory && <span>{parentCategory}</span>}</div>
			</div>
		</Link>
	);
};
